"use client";

import { useState, useRef, useEffect, memo } from "react";
import { Users, Eye, Star, ArrowUpRight, TrendingUp, TrendingDown } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

export interface SocialTrader {
  id?: string;
  address?: string;
  username: string;
  name?: string;
  image?: string;
  outcome?: string | null;
  side: string;
  sizeUsd: number;
  timestamp: string;
}

export interface MarketSocialActivity {
  friends: { count: number; traders: SocialTrader[] };
  watchlist: { count: number; traders: SocialTrader[] };
  leaders: { count: number; traders: SocialTrader[] };
  totalConnections: number;
  hasActivity: boolean;
}

interface SocialActivityIndicatorProps {
  activity?: MarketSocialActivity | null;
  variant?: "card" | "detail" | "compact";
  className?: string;
}

type ActivityType = "friends" | "watchlist" | "leaders";

const ACTIVITY_CONFIG: Record<
  ActivityType,
  {
    icon: React.ComponentType<{ size?: number; className?: string }>;
    label: string;
    labelPlural: string;
    title: string;
  }
> = {
  friends: {
    icon: Users,
    label: "fren",
    labelPlural: "frens",
    title: "Frens",
  },
  watchlist: {
    icon: Eye,
    label: "trader",
    labelPlural: "traders",
    title: "Tracked Wallets",
  },
  leaders: {
    icon: Star,
    label: "leader",
    labelPlural: "leaders",
    title: "Leaders",
  },
};

// Get priority activity type (friends > watchlist > leaders)
function getPrimaryActivity(
  activity: MarketSocialActivity
): { type: ActivityType; data: { count: number; traders: SocialTrader[] } } | null {
  if (activity.friends.count > 0) {
    return { type: "friends", data: activity.friends };
  }
  if (activity.watchlist.count > 0) {
    return { type: "watchlist", data: activity.watchlist };
  }
  if (activity.leaders.count > 0) {
    return { type: "leaders", data: activity.leaders };
  }
  return null;
}

// Format relative time
function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
}

// Format USD amount
function formatUsd(amount: number): string {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
  return `$${amount.toFixed(0)}`;
}

// Avatar component with fallback
function TraderAvatar({
  image,
  username,
  size = 36,
}: {
  image?: string;
  username: string;
  size?: number;
}) {
  const initials = username.slice(0, 2).toUpperCase();

  if (image) {
    return (
      <div
        className="relative rounded-[5px] overflow-hidden ring-1 ring-gray-200 dark:ring-white/10 flex-shrink-0"
        style={{ width: size, height: size }}
      >
        <Image
          src={image}
          alt={username}
          fill
          className="object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className="rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] ring-1 ring-gray-200 dark:ring-white/10 flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <span className="text-[10px] font-bold text-gray-400">{initials}</span>
    </div>
  );
}

// Stacked avatars component
function StackedAvatars({ traders, max = 3, size = 20 }: { traders: SocialTrader[]; max?: number; size?: number }) {
  const displayed = traders.slice(0, max);
  const remaining = traders.length - max;
  const spacing = size >= 30 ? '-space-x-2.5' : '-space-x-1.5';
  const fontSize = size >= 30 ? 'text-[10px]' : 'text-[8px]';

  return (
    <div className={`flex ${spacing}`}>
      {displayed.map((trader, i) => (
        <TraderAvatar
          key={trader.id || trader.address || i}
          image={trader.image}
          username={trader.username}
          size={size}
        />
      ))}
      {remaining > 0 && (
        <div
          className="rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] ring-1 ring-gray-200 dark:ring-white/10 flex items-center justify-center"
          style={{ width: size, height: size }}
        >
          <span className={`${fontSize} font-medium text-gray-400`}>+{remaining}</span>
        </div>
      )}
    </div>
  );
}

// Format usernames for card display
function formatTraderNames(traders: SocialTrader[], type: ActivityType): string {
  const config = ACTIVITY_CONFIG[type];
  if (traders.length === 0) return "";
  if (traders.length === 1) return traders[0].username;
  if (traders.length === 2) return `${traders[0].username} & ${traders[1].username}`;
  const remaining = traders.length - 2;
  const label = remaining === 1 ? config.label : config.labelPlural;
  return `${traders[0].username}, ${traders[1].username} +${remaining} ${label}`;
}

// Card variant - compact with avatars only, rich tooltip on hover/click
function CardVariant({
  activity,
  className = "",
  avatarSize = 25,
}: {
  activity: MarketSocialActivity;
  className?: string;
  avatarSize?: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const primary = getPrimaryActivity(activity);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  if (!primary) return null;

  const config = ACTIVITY_CONFIG[primary.type];
  const Icon = config.icon;

  return (
    <div ref={containerRef} className={`relative group/friends flex items-center ${className}`}>
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="cursor-pointer"
      >
        <StackedAvatars traders={primary.data.traders} max={3} size={avatarSize} />
      </div>
      {/* Tooltip */}
      <div className={`absolute bottom-full left-0 mb-2 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] shadow-xl shadow-black/8 dark:shadow-black/30 transition-opacity duration-150 z-[300] w-[280px] overflow-hidden ${
        isOpen ? "opacity-100 visible" : "opacity-0 invisible group-hover/friends:opacity-100 group-hover/friends:visible pointer-events-none"
      }`}>
        {/* Header */}
        <div className="px-4 pt-3.5 pb-2.5 flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center">
            <Icon size={12} className="text-gray-500 dark:text-gray-400" />
          </div>
          <span className="text-xs font-semibold text-gray-900 dark:text-white">
            {config.title}
          </span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-[#0f0f12] px-1.5 py-0.5 rounded-[5px] font-medium">
            {primary.data.count}
          </span>
        </div>
        {/* Divider */}
        <div className="mx-4 border-t border-gray-100 dark:border-[#1A1A1A]" />
        {/* Trader rows */}
        <div className="p-1.5">
          {primary.data.traders.slice(0, 5).map((trader, i) => {
            const isBuy = trader.side === "BUY";
            return (
              <div key={trader.id || trader.address || i} className="flex items-center gap-2.5 py-2 px-2.5 rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12]/60 transition-colors">
                <TraderAvatar image={trader.image} username={trader.username} size={32} />
                <div className="flex-1 min-w-0">
                  <span className="text-[12px] font-semibold text-gray-900 dark:text-white truncate block leading-tight">
                    {trader.name || trader.username}
                  </span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                      @{trader.username}
                    </span>
                    <span className="text-[8px] text-gray-300 dark:text-gray-600">·</span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                      {formatRelativeTime(trader.timestamp)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className={`flex items-center gap-0.5 px-2 py-1 rounded-[5px] text-[10px] font-bold ${
                    isBuy
                      ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400"
                  }`}>
                    {isBuy ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {isBuy ? "Buy" : "Sell"}
                  </div>
                  <span className="text-[11px] font-bold text-gray-900 dark:text-white">
                    {formatUsd(trader.sizeUsd)}
                  </span>
                </div>
              </div>
            );
          })}
          {primary.data.traders.length > 5 && (
            <div className="text-center py-1.5">
              <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">+{primary.data.traders.length - 5} more</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Individual trader row
function TraderRow({ trader, type }: { trader: SocialTrader; type: ActivityType }) {
  const isBuy = trader.side === "BUY";
  const profileHref = type === "friends"
    ? `/profile/${trader.username}`
    : `/traders/${trader.address || trader.username}`;

  return (
    <Link
      href={profileHref}
      className="group flex items-center gap-3 py-2.5 px-3 -mx-3 rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50 transition-colors"
    >
      {/* Avatar */}
      <TraderAvatar image={trader.image} username={trader.username} size={40} />

      {/* Name + username */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
            {trader.name || trader.username}
          </span>
          <ArrowUpRight className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          @{trader.username}
        </span>
      </div>

      {/* Trade info */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className={`flex items-center gap-1 px-2 py-1 rounded-[5px] text-xs font-bold ${
          isBuy
            ? "bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            : "bg-red-100 dark:bg-red-500/10 text-red-500 dark:text-red-400"
        }`}>
          {isBuy ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {isBuy ? "Buy" : "Sell"}
        </div>

        <span className="text-sm font-bold text-gray-900 dark:text-white min-w-[48px] text-right">
          {formatUsd(trader.sizeUsd)}
        </span>

        <span className="text-[10px] text-gray-400 dark:text-gray-500 min-w-[36px] text-right">
          {formatRelativeTime(trader.timestamp)}
        </span>
      </div>
    </Link>
  );
}

// Detail variant - full list with all categories
function DetailVariant({
  activity,
  className = "",
}: {
  activity: MarketSocialActivity;
  className?: string;
}) {
  const hasAnyActivity = activity.hasActivity;
  if (!hasAnyActivity) return null;

  // Collect all active categories
  const activeCategories: { type: ActivityType; data: { count: number; traders: SocialTrader[] } }[] = [];
  if (activity.friends.count > 0) activeCategories.push({ type: "friends", data: activity.friends });
  if (activity.watchlist.count > 0) activeCategories.push({ type: "watchlist", data: activity.watchlist });
  if (activity.leaders.count > 0) activeCategories.push({ type: "leaders", data: activity.leaders });

  return (
    <div className={`bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center">
            <Users className="w-4.5 h-4.5 text-gray-500 dark:text-gray-400" />
          </div>
          <div>
            <h3 className="font-monument text-sm text-gray-900 dark:text-white leading-tight">
              Your Network
            </h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              {activity.totalConnections} fren{activity.totalConnections !== 1 ? "s" : ""} trading this market
            </p>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="px-5 pb-5">
        {activeCategories.map(({ type, data }, catIdx) => {
          const config = ACTIVITY_CONFIG[type];
          const Icon = config.icon;

          return (
            <div key={type}>
              {/* Category separator */}
              {catIdx > 0 && (
                <div className="border-t border-gray-100 dark:border-[#1A1A1A] my-3" />
              )}

              {/* Category label */}
              <div className="flex items-center gap-2 mb-1">
                <Icon size={12} className="text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {config.title}
                </span>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {data.count}
                </span>
              </div>

              {/* Trader rows */}
              <div>
                {data.traders.map((trader, i) => (
                  <TraderRow
                    key={trader.id || trader.address || i}
                    trader={trader}
                    type={type}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SocialActivityIndicatorImpl({
  activity,
  variant = "card",
  className = "",
}: SocialActivityIndicatorProps) {
  if (!activity || !activity.hasActivity) {
    return null;
  }

  if (variant === "detail") {
    return <DetailVariant activity={activity} className={className} />;
  }

  if (variant === "compact") {
    return <CardVariant activity={activity} className={className} avatarSize={18} />;
  }

  return <CardVariant activity={activity} className={className} />;
}

// Memoised so card-level re-renders triggered by WS price ticks (which
// don't touch `activity`) skip walking the avatar list + activity-row
// subtree. The `activity` map entry from MarketsPageContent's
// `getEventSocialActivity` is stable across renders when nothing
// changed — default Object.is is fine; no custom comparator needed.
const SocialActivityIndicator = memo(SocialActivityIndicatorImpl);
SocialActivityIndicator.displayName = "SocialActivityIndicator";

export default SocialActivityIndicator;
